/**
 * useTimelines — hooks for the impact_timeline submodule API.
 *
 * GET    /api/timelines         → useTimelines()
 * POST   /api/timelines         → useBuildTimeline()
 * GET    /api/timelines/{id}    → useTimeline(id)
 * DELETE /api/timelines/{id}    → useDeleteTimeline()
 *
 * Export links (opened as window.location, not fetched via React Query):
 *   GET /api/timelines/{id}/export/pdf
 *   GET /api/timelines/{id}/export/csv
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/axios';
import { useUIStore } from '@/stores/uiStore';
import { useAuth } from '@/components/auth/AuthContext';
import { hasRole, ROLES } from '@/lib/constants';

/**
 * List all timelines for the current user (admins see all), sorted by
 * creation date descending.
 */
export function useTimelines() {
  const { isAuthenticated, user } = useAuth();
  const canAccess = isAuthenticated && hasRole(user?.role, ROLES.CONTRIBUTOR);
  return useQuery({
    queryKey: ['timelines'],
    enabled: canAccess,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get('/api/timelines', { signal });
      return data; // TimelineSummary[]
    },
    staleTime: 60_000,
  });
}

/**
 * Fetch a single timeline including its generated_narrative and filter_params.
 * @param {number|null} id
 */
export function useTimeline(id) {
  return useQuery({
    queryKey: ['timeline', id],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get(`/api/timelines/${id}`, { signal });
      return data; // Timeline
    },
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

/**
 * Build a new narrative timeline from filter parameters.
 * The backend queries matching Catalysts, computes aggregates, generates
 * HTML narrative, and persists an impact_timeline entity.
 */
export function useBuildTimeline() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    /**
     * @param {Object} payload  TimelineBuildRequest:
     *   label (required), region, date_from, date_to, event_types[], org_id
     */
    mutationFn: async (payload) => {
      const { data } = await apiClient.post('/api/timelines', payload);
      return data; // Timeline
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
      addToast({ title: 'Timeline built', variant: 'success' });
    },
    onError: (err) => {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Please try again.';
      addToast({
        title: `Build failed${status ? ` (${status})` : ''}`,
        description: msg,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Permanently delete a timeline (owner or admin only).
 */
export function useDeleteTimeline() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(`/api/timelines/${id}`);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
      queryClient.removeQueries({ queryKey: ['timeline', id] });
      addToast({ title: 'Timeline deleted', variant: 'success' });
    },
    onError: (err) => {
      addToast({
        title: 'Delete failed',
        description: err?.response?.data?.error || 'Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Returns the direct download URL for a timeline export.
 * The base URL is taken from the configured Axios instance base.
 */
export function getTimelineExportUrl(id, format) {
  const base = import.meta.env.VITE_DRUPAL_BASE_URL || '';
  return `${base}/api/timelines/${id}/export/${format}`;
}

/**
 * Fetch filtered catalysts for the timeline builder / contributors view.
 * Endpoint: GET /api/timeline/catalysts (note: singular "timeline", not "timelines")
 *
 * @param {Object} params  date_from, date_to, region, min_lives, organization_id,
 *                         event_types[], security_levels[], page, items_per_page
 * @param {Object} opts    { enabled }
 */
export function useTimelineCatalysts(params = {}, { enabled = true } = {}) {
  return useQuery({
    queryKey: ['timeline-catalysts', params],
    queryFn: async ({ signal }) => {
      const sp = new URLSearchParams();
      // Prefer pre-computed candidate IDs (stored on timeline at build time).
      if (params.candidate_ids?.length) {
        params.candidate_ids.forEach((id) => sp.append('candidate_id[]', String(id)));
      } else if (params.geo_bbox) {
        sp.set('geo_bbox[min_lon]', String(params.geo_bbox.min_lon));
        sp.set('geo_bbox[min_lat]', String(params.geo_bbox.min_lat));
        sp.set('geo_bbox[max_lon]', String(params.geo_bbox.max_lon));
        sp.set('geo_bbox[max_lat]', String(params.geo_bbox.max_lat));
      }
      if (params.date_from) sp.set('date_from', params.date_from);
      if (params.date_to) sp.set('date_to', params.date_to);
      if (params.region) sp.set('region', params.region);
      if (params.min_lives != null) sp.set('min_lives', String(params.min_lives));
      if (params.organization_id) sp.set('organization_id', String(params.organization_id));
      params.event_types?.forEach((t) => sp.append('event_type[]', t));
      params.security_levels?.forEach((l) => sp.append('security_level[]', l));
      sp.set('items_per_page', String(params.items_per_page ?? 100));
      sp.set('page', String(params.page ?? 1));
      const { data } = await apiClient.get(`/api/timeline/catalysts?${sp}`, { signal });
      return Array.isArray(data) ? data : [];
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}
